import React, { useState, useEffect } from 'react';
import { usePublicContent } from '../lib/publicContent';
import ImageZoom from './ImageZoom';
import TicketSubscription from './TicketSubscription';
import ModalComponent from './ModalComponent';
import { resolveMediaUrl } from '../lib/mediaUrl';
import Countdown from '../components/Countdown'
import GallerySlider from './GallerySlider';

const BodySection = () => {
  const { content } = usePublicContent();
  const about = content.about;
  const counters = content.counters;
  const schedule = content.schedule;
  const artists = content.artists;
  const gallery = content.gallery;
  const tickets = content.tickets;
  const reservations = content.reservations;
  const contact = content.contact;
  
  // Artist modal
  const [showModal, setShowModal] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const openModal = (artist) => { setSelectedArtist(artist); setShowModal(true); };
  const closeModal = () => { setSelectedArtist(null); setShowModal(false); };

  const visibleDays = (schedule?.days || []).filter(d => d.enabled !== false);
  const [activeTab, setActiveTab] = useState(visibleDays?.[0]?.id || 'day1');

  // When the day list changes (fallback content → API content), always start on the first day.
  // Day ids are editable in the admin, so the fallback's first id may be a later day in the API data.
  useEffect(() => {
    if (visibleDays.length > 0) {
      setActiveTab(visibleDays[0].id);
    }
  }, [visibleDays.map(d => d.id).join(',')]); // eslint-disable-line

  useEffect(() => {
  const HEADER_OFFSET = 80; // adjust to your fixed header height

  const scrollToHash = (smooth = false) => {
    const { hash } = window.location;
    if (!hash) return false;

    // Decode e.g. %C3%BC etc.
    const id = decodeURIComponent(hash.slice(1));
    const el = document.getElementById(id);
    if (!el) return false;

    const y = el.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;
    window.scrollTo({ top: y, behavior: smooth ? 'smooth' : 'auto' });
    return true;
  };

  // Try on load / after render
  let tried = 0;
  const tryScroll = () => {
    if (scrollToHash(tried > 0)) return;
    if (tried < 6) { // retry for ~600ms total
      tried += 1;
      setTimeout(tryScroll, 100);
    }
  };

  // On first mount after sections exist
  tryScroll();

  // On hash changes (e.g., user clicks in-site anchor)
  const onHash = () => scrollToHash(true);
  window.addEventListener('hashchange', onHash);

  return () => window.removeEventListener('hashchange', onHash);
  // Depend on the section IDs so this reruns when content arrives
}, [
  about?.sectionId,
  schedule?.sectionId,
  artists?.sectionId,
  gallery?.sectionId,
  tickets?.sectionId,
  reservations?.sectionId
]);

  return (
    <>
      {/* ABOUT */}
      <section id={about.sectionId} className="about-event section-padding">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="section-title-header text-center">
                <h1 className="section-title wow fadeInUp" data-wow-delay="0.2s">{about.title}</h1>
              </div>
            </div>
            <div className="col-md-6 col-lg-6 col-xs-12 wow fadeInRight d-flex justify-content-center align-items-center" data-wow-delay="0.3s">
              <img className="img-fluid" src={resolveMediaUrl(about.imageUrl)} alt="" />
            </div>
            <div className="col-md-6 col-lg-6 col-xs-12 wow fadeInLeft text-justify" data-wow-delay="0.3s">
              <p className="intro-desc wow fadeInUp" style={{ fontSize: '21px', fontWeight: 700, margin: '10px 0' }} data-wow-delay="0.3s">
                {about.greetingTitle}
              </p>
              {about.paragraphs?.map((p, i) => (
                <p key={i} className="intro-desc wow fadeInUp" data-wow-delay="0.3s">{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COUNTDOWN */}
      <section className="countdown-timer section-padding">
        <div className="container">
          <div className="row text-center">
            <div className="col-md-12 col-sm-12 col-xs-12">
              <div className="heading-count">
                <h2 className="wow fadeInDown" data-wow-delay="0.2s">{content.countdown.title}</h2>
              </div>
            </div>
            <div className="col-md-12 col-sm-12 col-xs-12">
              <div className="row time-countdown justify-content-center wow fadeInUp" data-wow-delay="0.2s">
                <Countdown targetISO={content.countdown.targetISO} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COUNTERS */}
      <section className="counter-section section-padding">
        <div className="container">
          <div className="row">
            {/* Location */}
            <div className="col-md-6 col-lg-3 col-xs-12 work-counter-widget text-center">
              <div className="counter wow fadeInRight" data-wow-delay="0.3s">
                <div className="icon"><i className={counters.location.icon}></i></div>
                <p>{counters.location.venue}</p>
                <span>{counters.location.city}</span>
              </div>
            </div>
            {/* Dates */}
            <div className="col-md-6 col-lg-3 col-xs-12 work-counter-widget text-center">
              <div className="counter wow fadeInRight" data-wow-delay="0.6s">
                <div className="icon"><i className={counters.dates.icon}></i></div>
                <p>{counters.dates.range}</p>
                {counters.dates.days.map((d, i) => (
                  <p key={i}><span>{d.label}: {d.start}, Einlass: {d.doors}</span></p>
                ))}
              </div>
            </div>
            {/* Seats */}
            <div className="col-md-6 col-lg-3 col-xs-12 work-counter-widget text-center">
              <div className="counter wow fadeInRight" data-wow-delay="0.9s">
                <div className="icon"><i className={counters.seats.icon}></i></div>
                <p>{counters.seats.label}</p>
                <span>{counters.seats.sub}</span>
              </div>
            </div>
            {/* Extras */}
            <div className="col-md-6 col-lg-3 col-xs-12 work-counter-widget text-center">
              <div className="counter wow fadeInRight" data-wow-delay="1.2s">
                <div className="icon"><i className={counters.extras.icon}></i></div>
                <p>{counters.extras.label}</p>
                <span>{counters.extras.sub}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SCHEDULE */}
<section id={schedule.sectionId} className="schedule section-padding">
  <div className="container">
    <div className="row"><div className="col-12">
      <div className="section-title-header text-center">
        <h1 className="section-title wow fadeInUp" data-wow-delay="0.2s">{schedule.title}</h1>
      </div>
    </div></div>

    <div className="schedule-area row wow fadeInDown" data-wow-delay="0.3s">
      {/* Tabs */}
      <div className="schedule-tab-title col-md-3 col-lg-3 col-xs-12">
        <div className="table-responsive" style={{ overflowX: 'unset' }}>
          <ul className="nav nav-tabs" id="myTab" role="tablist">
            {visibleDays.map((d) => (
              <li className="nav-item" key={d.id}>
                <a className={`nav-link ${activeTab === d.id ? 'active' : ''}`} role="tab"
                  aria-controls={d.id} aria-selected={activeTab === d.id}
                  onClick={() => setActiveTab(d.id)}>
                  <div className="item-text">
                    <h4>{d.tabLabel}</h4>
                    <h5>{d.dateLabel}</h5>
                  </div>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Content */}
      <div className="schedule-tab-content col-md-9 col-lg-9 col-xs-12 clearfix">
        <style>{`
          .prog-authors-list { margin: 4px 0 8px; }
          .prog-authors-item { font-style: italic; opacity:.9; }
          .perf-row { display:flex; flex-wrap:wrap; gap:12px 16px; align-items:flex-start; }
          .perf-name { font-weight:700; }
          .perf-inst { opacity:.8; margin-left:6px; }
          .pause-block { margin: 12px 0; padding: 8px 12px; background: #f7f7f9; border-radius: 8px; font-weight: 600; }
        `}</style>

        <div className="tab-content" id="myTabContent">
          {visibleDays.map((d) => {
            // Legacy fallback if program missing
            const legacyProgram = [{
              kind: 'work',
              title: d.title,
              authors: (d.items && d.items[0]) ? [d.items[0]] : [],
              performers: (d.items || []).slice(1).map((line) => {
                const m = String(line).split(/[,–-]\s*/);
                return { name: m[0] || '', instrument: m.slice(1).join(' - ') || '' };
              })
            }];

            // Normalize each entry to ensure authors is always an array, performers is always an array of {name,instrument}
            const programRaw = Array.isArray(d.program) && d.program.length ? d.program : legacyProgram;
            const program = programRaw.map((b) => ({
              kind: b?.kind === 'pause' ? 'pause' : 'work',
              title: b?.title || (b?.kind === 'pause' ? 'Pause' : ''),
              authors: Array.isArray(b?.authors)
                ? b.authors.filter(Boolean)
                : (b?.authors ? [String(b.authors)] : []),
              performers: Array.isArray(b?.performers)
                ? b.performers.map((p) => ({ name: p?.name || '', instrument: p?.instrument || '' }))
                : []
            }));

            return (
              <div key={d.id} className={`tab-pane fade ${activeTab === d.id ? 'active show' : ''}`} id={d.id} role="tabpanel">
                <div id="accordion">
                  <div className="card">
                    <div id={`heading-${d.id}`}>
                      <div className="collapsed card-header">
                        <span className="time">Beginn: {d.time.start}, Einlass: {d.time.doors}</span>
                        {d.title && <h4 style={{ marginTop: 10 }}>{d.title}</h4>}

                        {program.map((b, i) => (
                          <div key={i} className="mb-3">
                            {b.kind === 'pause' ? (
                              <div className="pause-block">{b.title || 'Pause'}</div>
                            ) : (
                              <>
                                {b.title && <h5 className="mt-2">{b.title}</h5>}

                                {/* Werke-Teile vertically */}
                                {b.authors.length > 0 && (
                                  <div className="prog-authors-list">
                                    {b.authors.map((line, idx) => (
                                      <div key={idx} className="prog-authors-item">{line}</div>
                                    ))}
                                  </div>
                                )}

                                {/* Performers horizontally */}
                                {b.performers.length > 0 && (
                                  <div className="perf-row">
                                    {b.performers.map((p, j) => (
                                      <span key={j}>
                                        <span className="perf-name">{p.name}</span>
                                        {p.instrument && <span className="perf-inst">— {p.instrument}</span>}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </div>
</section>


      {/* ARTISTS */}
      <section id={artists.sectionId} className="section-padding text-center">
        <div className="container">
          <div className="row"><div className="col-12">
            <div className="section-title-header text-center">
              <h1 className="section-title wow fadeInUp" data-wow-delay="0.2s">{artists.title}</h1>
            </div>
          </div></div>

          <div className="row">
            {artists.entries.map((a, idx) => (
              <div className="col-sm-6 col-md-6 col-lg-4" key={idx}>
                <div className="team-item wow fadeInUp" data-wow-delay="0.2s">
                  <div className="team-img">
                    <img className="img-fluid" src={resolveMediaUrl(a.imageUrl)} alt="" />
                    <div className="team-overlay">
                      <div className="overlay-social-icon text-center">
                        <ul className="social-icons">
                          {a.website && (
                            <li>
                              <a href={a.website} target="_blank" rel="noreferrer">
                                <i className="lni-website" aria-hidden="true"></i>
                              </a>
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="info-text">
                    <h3><a onClick={() => openModal(a)}>{a.name}</a></h3>
                    <p>{a.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <ModalComponent
          showModal={showModal}
          closeModal={closeModal}
          content={
            selectedArtist && {
              image: selectedArtist.imageUrl,
              title: (
                <div className="info-text">
                  <h3>{selectedArtist.name}</h3>
                  <p>{selectedArtist.role}</p>
                </div>
              ),
              maincontent: (
                <>
                  {selectedArtist.bioParagraphs?.map((p, i) => (
                    <p key={i} className={i === 1 ? 'middle-p' : ''}>{p}</p>
                  ))}
                </>
              ),
            }
          }
        />
      </section>

      {/* GALLERY */}
      <section id={gallery.sectionId} className="section-padding">
        <div className="container">
          <div className="row"><div className="col-12">
            <div className="section-title-header text-center">
              <h1 className="section-title wow fadeInUp" data-wow-delay="0.2s">{gallery.title}</h1>
            </div>
          </div></div>

          <GallerySlider images={gallery.images} />
        </div>
      </section>

      {/* TICKETS */}
      <section id={tickets.sectionId} className="section-padding">
        <div className="container">
          <div className="row"><div className="col-12">
            <div className="section-title-header text-center">
              <h1 className="section-title wow fadeInUp" data-wow-delay="0.2s">{tickets.title}</h1>
            </div>
          </div></div>

          <div className="row">
            {tickets.tiers.map((t, i) => (
              <div key={i} className="col-lg-4 col-sm-6 col-xa-12 mb-3">
                <div className={`price-block-wrapper wow ${i===0?'fadeInLeft':i===1?'fadeInUp':'fadeInRight'}`} data-wow-delay={`${0.2 + i*0.1}s`}>
                  <div className="icon"><i className={t.icon}></i></div>
                  <div className="colmun-title"><h5>{t.title}</h5></div>
                  <div className="price"><h2>{t.price}</h2></div>
                  <a href={tickets.ctaAnchor} className="btn btn-common">Ticket Reservieren</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RESERVATIONS */}
      <section id={reservations.sectionId} className="section-padding">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12">
              <div className="section-title-header text-center">
                <h1 className="section-title wow fadeInUp" data-wow-delay="0.2s">{reservations.title}</h1>
              </div>
            </div>
            <TicketSubscription />
          </div>
        </div>
      </section>

      {/* CONTACT / MAP */}
      <section id="Kontakt">
        <div className="container-fluid">
          <div className="row"><div className="col-12">
            <meta name="robots" content="noindex, nofollow" />
            <object style={{ border: '0', height: '450px', width: '100%' }} data={contact.mapEmbedUrl}></object>
          </div></div>
        </div>
      </section>
    </>
  );
};

export default BodySection;
